class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute("handle");
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Prevent multiple initializations
    if (this.swiperInitialized) return;
    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", () => this.init());
      window.addEventListener("shopify:section:select", () => this.init());

      // fallback initialization after delay in design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      window.addEventListener("load", () => this.init());
    }
  }

  disconnectedCallback() {
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Options before initialization
   */
  setOptions() {
    if (this.debug) console.log('setOptions');
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    if (this.debug) console.log('getOptionsAsJsonScripts');
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};
    if (jsonScriptAutomated && jsonScriptAutomated.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptAutomated.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptAutomated?.textContent);
      }
    }
    if (jsonScriptOverwrite && jsonScriptOverwrite.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptOverwrite.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptOverwrite?.textContent);
      }
    }
    return options;
  }

  /** thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.debug) console.log('setInteractions');
  }
}
// customElements.define('instance-swiper', InstanceSwiper);
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;
    if (this.debug) console.log('setCenteredSlides');

    const afterInit = (swiper) => {
      if (swiper.activeIndex !== 0) swiper.slideTo(0);
      if (this.debug) console.log('afterInit');
      const swiperWrapper = swiper.wrapperEl;
      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });
      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
          if (this.debug) console.log('swiperWrapper observer removed styles!');
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
              if (this.debug) console.log('swiperWrapper observer disconnect!');
            }
          }, 500);
        }
      }

      /**
       * Quick fix for thumbnails dislocationing issue
       * when thumbnails are less than the height of the wrapper
       */
      const calculateAllThumbHeight = (this.querySelector(".swiper-slide").clientHeight + 16) * this.querySelectorAll(".swiper-slide").length;
      const thumbsWrapper = this.parentElement;
      function setThumbsWrapperHeightToAuto() {
        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = (calculateAllThumbHeight - 16) + "px";
        } else {
          thumbsWrapper.removeAttribute("style");
        }
      }
      window.addEventListener("resize", setThumbsWrapperHeightToAuto);
      // have to wait global.js media area height css variables to be generated
      setTimeout(() => {
        setThumbsWrapperHeightToAuto();
      }, 2500);
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit,
      },
    };
  }

}
if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute("thumbs");
    this.isZoomActive = this.hasAttribute("zoom");
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ["thumbs", "zoom"];
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    if (this.debug) console.log("setThumbOptions");
    const thumbsInstance = super.getThumbsInstance();
    if (this.debug) console.log("thumbsInstance", thumbsInstance);
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    this.initModelViewer();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on("slideChange", swiper => {
      if (window.innerWidth < 750) {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated manually on mobile.");
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on("scroll", swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated after scroll.");
      }, 200);
    });
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;
      if (nextEl) {
        nextEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
      if (prevEl) {
        prevEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance.allowTouchMove = true;
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true (navigation/thumb clicks)
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        this.modelViewerBtn.classList.remove('is-active');
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    if (this.debug) console.log("setThumbsInteraction");
    const thumbsSwiper = this.options.thumbs.swiper;

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      this.modelViewerBtn.classList.remove('btn--active');
    });

    this.instance.on("slideChangeTransitionStart", function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (this.debug) console.log("slideChangeTransitionStart", activeIndex, thumbsActiveIndex);
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    const [
      zoomContainerWidth,
      zoomContainerHeight,
      closeIcon,
      prevIcon,
      nextIcon
    ] = [
      this.offsetWidth,
      this.offsetHeight,
      document.querySelector("[data-close-icon]"),
      document.querySelector("[data-prev-icon]"),
      document.querySelector("[data-next-icon]")
    ];

    function isPhone() {
      return window.innerWidth < 750;
    }

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: "a.product__gallery-toggle",
      mainClass: "pswp--product-media-gallery",

      loop: false,
      showHideAnimationType: "zoom",

      // Always open fitted to the available viewport.
      initialZoomLevel: zoomLevelObject => zoomLevelObject.fit,

      // IMPORTANT: make double click / double tap visually consistent.
      // Using a multiple of FIT (instead of native pixel scale = 1) means
      // a 2000px and a 4000px image both jump to the same apparent 2x zoom.
      secondaryZoomLevel: zoomLevelObject => zoomLevelObject.fit * 2,

      // Keep the already-working mobile pinch behaviour essentially unchanged.
      // Desktop gets a predictable 4x visual range for the custom slider below.
      maxZoomLevel: zoomLevelObject => isPhone()
        ? Math.max(zoomLevelObject.fit * 2, 1.5)
        : zoomLevelObject.fit * 4,

      allowPanToNext: false,
      pinchToClose: false,
      doubleTapAction: (point, originalEvent) => {
        const pswp = photoSwipeLightboxInstance.pswp;
        const slide = pswp?.currSlide;
        if (!slide) return false;

        const fit = slide.zoomLevels.fit;
        const zoomed = slide.currZoomLevel > fit * 1.04;
        const target = zoomed
          ? fit
          : Math.min(fit * 2, slide.zoomLevels.max);

        slide.zoomTo(
          target,
          zoomed ? pswp.getViewportCenterPoint() : point,
          220
        );
        return false;
      },
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter(
      "uiElement",
      (element, data) => {
        if (data.name === "close") {
          element.innerHTML = closeIcon.innerHTML;
        } else if (data.name === "arrowPrev") {
          element.innerHTML = prevIcon.innerHTML;
        } else if (data.name === "arrowNext") {
          element.innerHTML = nextIcon.innerHTML;
        }
        return element;
      }
    );

    // html for video
    photoSwipeLightboxInstance.addFilter(
      "itemData",
      (itemData, index) => {
        if (itemData.type === "html" && itemData.element) {
          return {
            html: itemData.element.dataset.pswpHtml
          };
        }
        return itemData;
      }
    );


    photoSwipeLightboxInstance.init();

    // MOBILE ZOOM EXIT FIX V2
    // Bind directly to PhotoSwipe's live DOM after it opens. This avoids relying
    // on optional PhotoSwipe pointer event hooks and does not change pinch-in.
    photoSwipeLightboxInstance.on('afterInit', () => {
      if (!isPhone()) return;

      const pswp = photoSwipeLightboxInstance.pswp;
      const root = pswp?.element;
      if (!root || root.dataset.mobileZoomExitFix === 'true') return;
      root.dataset.mobileZoomExitFix = 'true';

      const touches = new Set();

      const settleToFitIfNeeded = () => {
        const slide = pswp.currSlide;
        if (!slide) return;

        const fit = slide.zoomLevels.fit;
        const current = slide.currZoomLevel;

        // Once the user has pinched back very close to normal, finish the reset
        // cleanly and center it. Intentional zoom levels remain untouched.
        if (current <= fit * 1.12) {
          slide.zoomTo(fit, pswp.getViewportCenterPoint(), 160);
        }
      };

      root.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') touches.add(event.pointerId);
      }, { passive: true });

      const release = (event) => {
        if (event.pointerType !== 'touch') return;
        touches.delete(event.pointerId);
        if (touches.size === 0) {
          window.requestAnimationFrame(settleToFitIfNeeded);
        }
      };

      root.addEventListener('pointerup', release, { passive: true });
      root.addEventListener('pointercancel', release, { passive: true });
    });


    // Desktop-only zoom slider. Mobile is intentionally left untouched: users
    // can keep using pinch-to-zoom exactly as before. The slider maps 0-100%
    // from fit-to-screen to the configured desktop max zoom level.
    let desktopZoomSlider = null;
    let desktopZoomPercent = null;
    let sliderIsActive = false;

    const updateDesktopZoomSlider = () => {
      const pswp = photoSwipeLightboxInstance.pswp;
      const slide = pswp?.currSlide;
      if (!desktopZoomSlider || !slide || sliderIsActive) return;

      const fit = slide.zoomLevels.fit;
      const max = slide.zoomLevels.max;
      const current = slide.currZoomLevel;
      const range = max - fit;
      const percent = range > 0
        ? Math.round(((current - fit) / range) * 100)
        : 0;
      const clamped = Math.max(0, Math.min(100, percent));

      desktopZoomSlider.value = String(clamped);
      if (desktopZoomPercent) desktopZoomPercent.textContent = `${clamped}%`;
    };

    photoSwipeLightboxInstance.on('afterInit', () => {
      if (isPhone()) return;

      const pswp = photoSwipeLightboxInstance.pswp;
      if (!pswp?.element || pswp.element.querySelector('.pswp__desktop-zoom-control')) return;

      const control = document.createElement('div');
      control.className = 'pswp__desktop-zoom-control';
      control.setAttribute('aria-label', 'Control de zoom');
      control.innerHTML = `
        <span class="pswp__desktop-zoom-symbol" aria-hidden="true">−</span>
        <input
          class="pswp__desktop-zoom-slider"
          type="range"
          min="0"
          max="100"
          step="1"
          value="0"
          aria-label="Nivel de zoom"
        >
        <span class="pswp__desktop-zoom-symbol" aria-hidden="true">+</span>
        <span class="pswp__desktop-zoom-percent" aria-live="polite">0%</span>
      `;

      pswp.element.appendChild(control);
      desktopZoomSlider = control.querySelector('.pswp__desktop-zoom-slider');
      desktopZoomPercent = control.querySelector('.pswp__desktop-zoom-percent');

      const applySliderZoom = () => {
        const slide = pswp.currSlide;
        if (!slide) return;

        const fit = slide.zoomLevels.fit;
        const max = slide.zoomLevels.max;
        const ratio = Number(desktopZoomSlider.value) / 100;
        const targetZoom = fit + ((max - fit) * ratio);

        slide.zoomTo(targetZoom, pswp.getViewportCenterPoint(), 0);
        desktopZoomPercent.textContent = `${desktopZoomSlider.value}%`;
      };

      desktopZoomSlider.addEventListener('pointerdown', () => {
        sliderIsActive = true;
      });
      desktopZoomSlider.addEventListener('pointerup', () => {
        sliderIsActive = false;
        updateDesktopZoomSlider();
      });
      desktopZoomSlider.addEventListener('change', () => {
        sliderIsActive = false;
        updateDesktopZoomSlider();
      });
      desktopZoomSlider.addEventListener('input', applySliderZoom);

      updateDesktopZoomSlider();
    });

    photoSwipeLightboxInstance.on('zoomPanUpdate', updateDesktopZoomSlider);
    photoSwipeLightboxInstance.on('change', () => {
      window.requestAnimationFrame(updateDesktopZoomSlider);
    });

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    let originalViewportContent = null;

    const lockPageZoom = () => {
      document.documentElement.classList.add('pswp-open');
      document.body.classList.add('pswp-open', 'overflow-hidden');

      // Prevent the browser from interpreting a pinch inside the product
      // gallery/lightbox as page zoom. Restore the original viewport on close.
      if (viewportMeta) {
        originalViewportContent = viewportMeta.getAttribute('content');
        viewportMeta.setAttribute(
          'content',
          'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no'
        );
      }
    };

    const unlockPageZoom = () => {
      document.documentElement.classList.remove('pswp-open');
      document.body.classList.remove('pswp-open', 'overflow-hidden');

      if (viewportMeta && originalViewportContent !== null) {
        viewportMeta.setAttribute('content', originalViewportContent);
        originalViewportContent = null;
      }
    };

    photoSwipeLightboxInstance.on("beforeOpen", () => {
      lockPageZoom();
      const videos = this.querySelectorAll("video");
      Array.from(videos).forEach(video => {
        // if video is not playing, call playPromise
        // force to play
        video
          .play()
          .then(() => {
            // Automatic playback started!
            // Show playing UI.
            // video.play();
          })
          .catch(error => {
            // Auto-play was prevented
            // Show paused UI.
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on("closingAnimationStart", () => {
      unlockPageZoom();
    });
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(
      this.querySelectorAll("[data-media-id]")
    ).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}


class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
    window.addEventListener("resize", this.init.bind(this));
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider').offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && window.innerWidth > 750) {
      containerOffsetWidth = document.querySelector('.main-product__media--grid-item').offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const mediaInfoTextWidth = this.querySelector('p:last-child').offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${mediaInfoTextWidth / maxWidthForInfo * 8}s`;
      mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}


